import React, { useState, useMemo, useEffect } from 'react';
import {
  Search, Plus, Download, Eye, Edit3, X, XCircle,
  CheckCircle, Users, TrendingUp, CreditCard, Zap,
  Shield, Headphones, Check, Minus, ToggleLeft, ToggleRight,
} from 'lucide-react';

import BaseModal from '../../../components/BaseModal/BaseModal';
import StatusBadge, { type StatusVariant } from '../../../components/StatusBadge/StatusBadge';
import Button from '../../../components/Button/Button';
import DataTable, { type Column } from '../../../components/DataTable/DataTable';
import Pagination from '../../../components/Pagination/Pagination';

import styles from './Subscriptions.module.css';
import toast from 'react-hot-toast';
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Plan {
  id: string;
  name: string;
  slug: 'starter' | 'standard' | 'advanced' | string;
  monthlyPrice: number;
  yearlyPrice: number;
  maxApis: number;    
  maxAgents: number;  
  modules: string[];
  supportLevel: string;
  status: 'Active' | 'Inactive';
  subscribers: number;
  mrr: number;
  created: string;
}

interface Subscriber {
  id: string;
  tenantName: string;
  email: string;
  plan: string;
  status: 'Active' | 'Trial' | 'Expired' | 'Cancelled';
  billingCycle: 'Monthly' | 'Yearly';
  amount: number;
  nextBilling: string;
  started: string;
}

interface ModuleOption {
  id: string;
  name: string;
  status: string;
}

// fallback plans (agar API down ho)
const INITIAL_PLANS: Plan[] = [
  {  
    id: 'PLN-001',
    name: 'Starter',
    slug: 'starter',
    monthlyPrice: 99,
    yearlyPrice: 79,
    maxApis: 1,    
    maxAgents: 5,
    modules: ['Flight Engine', 'Home Page', 'About Page', 'Contact Page', 'Special Offers', 'Query Form'],
    supportLevel: 'Email',
    status: 'Active',
    subscribers: 5,
    mrr: 495,
    created: 'Jan 1, 2025',
  },
  {
    id: 'PLN-002',
    name: 'Standard',
    slug: 'standard',
    monthlyPrice: 149,
    yearlyPrice: 119,
    maxApis: 3,
    maxAgents: 15,
    modules: ['Flight Engine', 'Package Engine', 'Home Page', 'About Page', 'Contact Page', 'Special Offers', 'Query Form', 'Custom Theme'],
    supportLevel: 'Priority Email',
    status: 'Active',
    subscribers: 6,
    mrr: 894,
    created: 'Jan 1, 2025',
  },
  {
    id: 'PLN-003',
    name: 'Advanced',
    slug: 'advanced',
    monthlyPrice: 199,
    yearlyPrice: 159,
    maxApis: 5,
    maxAgents: 50,
    modules: ['Flight Engine', 'Package Engine', 'Home Page', 'About Page', 'Contact Page', 'Special Offers', 'Query Form', 'Custom Theme', 'Visa Module', 'Reports', 'B2B Portal'],
    supportLevel: 'Dedicated Account Manager',
    status: 'Active',
    subscribers: 4,
    mrr: 796,
    created: 'Jan 1, 2025',
  },
];

const SUBSCRIBERS: Subscriber[] = [
  { id: 'SUB-001', tenantName: 'Al-Safar Travel', email: 'admin@alsafar.ae', plan: 'Advanced', status: 'Active', billingCycle: 'Monthly', amount: 199, nextBilling: 'Apr 12, 2025', started: 'Jan 12, 2025' },
  { id: 'SUB-002', tenantName: 'JetStream Agency', email: 'ops@jetstream.pk', plan: 'Standard', status: 'Active', billingCycle: 'Monthly', amount: 149, nextBilling: 'Apr 28, 2025', started: 'Jan 28, 2025' },
  { id: 'SUB-003', tenantName: 'SkyRoutes Travel', email: 'hello@skyroutes.io', plan: 'Standard', status: 'Active', billingCycle: 'Yearly', amount: 119, nextBilling: 'Feb 3, 2026', started: 'Feb 3, 2025' },
  { id: 'SUB-004', tenantName: 'Nexvoyage Ltd', email: 'admin@nexvoyage.com', plan: 'Starter', status: 'Cancelled', billingCycle: 'Monthly', amount: 99, nextBilling: '—', started: 'Feb 10, 2025' },
  { id: 'SUB-005', tenantName: 'Bajij Links', email: 'info@bajijlinks.pk', plan: 'Starter', status: 'Trial', billingCycle: 'Monthly', amount: 0, nextBilling: 'Apr 18, 2025', started: 'Feb 18, 2025' },
  { id: 'SUB-006', tenantName: 'Horizon Tours', email: 'hello@horizontours.ae', plan: 'Advanced', status: 'Active', billingCycle: 'Monthly', amount: 199, nextBilling: 'Apr 1, 2025', started: 'Mar 1, 2025' },
  { id: 'SUB-007', tenantName: 'Wanderlust Co.', email: 'admin@wanderlust.co', plan: 'Standard', status: 'Expired', billingCycle: 'Monthly', amount: 149, nextBilling: '—', started: 'Mar 5, 2025' },
  { id: 'SUB-008', tenantName: 'SwiftWings Travel', email: 'ops@swiftwings.sa', plan: 'Advanced', status: 'Active', billingCycle: 'Yearly', amount: 159, nextBilling: 'Mar 9, 2026', started: 'Mar 9, 2025' },
  { id: 'SUB-009', tenantName: 'Travelwala.com', email: 'admin@travelwala.com', plan: 'Standard', status: 'Active', billingCycle: 'Monthly', amount: 149, nextBilling: 'Apr 11, 2025', started: 'Mar 11, 2025' },
  { id: 'SUB-010', tenantName: 'JetTrail Travels', email: 'ceo@jettrail.net', plan: 'Starter', status: 'Cancelled', billingCycle: 'Monthly', amount: 99, nextBilling: '—', started: 'Mar 13, 2025' },
  { id: 'SUB-011', tenantName: 'BlueSky Adventures', email: 'hi@bluesky.ae', plan: 'Advanced', status: 'Active', billingCycle: 'Monthly', amount: 199, nextBilling: 'Apr 14, 2025', started: 'Mar 14, 2025' },
  { id: 'SUB-012', tenantName: 'PeakTravel IN', email: 'admin@peaktravel.in', plan: 'Starter', status: 'Trial', billingCycle: 'Monthly', amount: 0, nextBilling: 'Apr 15, 2025', started: 'Mar 15, 2025' },
  { id: 'SUB-013', tenantName: 'FarHorizon Ltd', email: 'ops@farhorizon.co.uk', plan: 'Standard', status: 'Active', billingCycle: 'Monthly', amount: 149, nextBilling: 'Apr 18, 2025', started: 'Mar 18, 2025' },
  { id: 'SUB-014', tenantName: 'GoldenRoute Tours', email: 'admin@goldenroute.sa', plan: 'Standard', status: 'Active', billingCycle: 'Yearly', amount: 119, nextBilling: 'Mar 20, 2026', started: 'Mar 20, 2025' },
  { id: 'SUB-015', tenantName: 'TripNest Agency', email: 'hello@tripnest.in', plan: 'Starter', status: 'Trial', billingCycle: 'Monthly', amount: 0, nextBilling: 'Apr 22, 2025', started: 'Mar 22, 2025' },
];

function subStatusVariant(s: Subscriber['status']): StatusVariant {
  const map: Record<string, StatusVariant> = {
    Active: 'active',
    Trial: 'trial',
    Expired: 'expired',
    Cancelled: 'suspended',
  };
  return map[s] || 'info';
}

const PlanCard: React.FC<{
  plan: Plan;
  onEdit: (p: Plan) => void;
  onDeactivate: (p: Plan) => void;
  onView: (p: Plan) => void;
}> = ({ plan, onEdit, onDeactivate, onView }) => {
  const isAdvanced = plan.slug === 'advanced';
  return (
    <div className={`${styles.planCard} ${isAdvanced ? styles.planCardFeatured : ''}`}>
      {isAdvanced && <div className={styles.planCardBadge}>Most Popular</div>}
      <div className={styles.planCardHeader}>
        <div className={styles.planCardNameRow}>
          <h3 className={styles.planCardName}>{plan.name}</h3>
          <span className={`${styles.planCardStatus} ${plan.status === 'Active' ? styles.planStatusActive : styles.planStatusInactive}`}>
            {plan.status}
          </span>
        </div>
        <div className={styles.planCardPricing}>
          <span className={styles.planCardPrice}>${plan.monthlyPrice}</span>
          <span className={styles.planCardPriceUnit}>/mo</span>
          <span className={styles.planCardYearly}>${plan.yearlyPrice}/mo billed yearly</span>
        </div>
      </div>

      <div className={styles.planCardStats}>
        <div className={styles.planCardStat}>
          <span className={styles.planCardStatValue}>{plan.subscribers}</span>
          <span className={styles.planCardStatLabel}>Subscribers</span>
        </div>
        <div className={styles.planCardStatDivider} />
        <div className={styles.planCardStat}>
          <span className={styles.planCardStatValue}>${plan.mrr.toLocaleString()}</span>
          <span className={styles.planCardStatLabel}>MRR</span>
        </div>
        <div className={styles.planCardStatDivider} />
        <div className={styles.planCardStat}>
          <span className={styles.planCardStatValue}>{plan.maxAgents}</span>
          <span className={styles.planCardStatLabel}>Max Agents</span>
        </div>
      </div>

      <div className={styles.planCardLimits}>
        <div className={styles.planCardLimitRow}>
          <span className={styles.planCardLimitLabel}>APIs Allowed</span>
          <span className={styles.planCardLimitValue}>{plan.maxApis}</span>
        </div>
        <div className={styles.planCardLimitRow}>
          <span className={styles.planCardLimitLabel}>Max Agents</span>
          <span className={styles.planCardLimitValue}>{plan.maxAgents}</span>
        </div>
        <div className={styles.planCardLimitRow}>
          <span className={styles.planCardLimitLabel}>Modules</span>
          <span className={styles.planCardLimitValue}>{plan.modules.length}</span>
        </div>
        <div className={styles.planCardLimitRow}>
          <span className={styles.planCardLimitLabel}>Support</span>
          <span className={styles.planCardLimitValue}>{plan.supportLevel}</span>
        </div>
      </div>

      <div className={styles.planCardActions}>
        <button className={styles.planCardBtn} onClick={() => onView(plan)}><Eye size={13} /> View</button>
        <button className={styles.planCardBtn} onClick={() => onEdit(plan)}><Edit3 size={13} /> Edit</button>
        <button className={`${styles.planCardBtn} ${styles.planCardBtnDanger}`} onClick={() => onDeactivate(plan)}>
          <XCircle size={13} /> Delete
        </button>
      </div>
    </div>
  );
};

const PlanDetailDrawer: React.FC<{
  plan: Plan | null;
  modules: ModuleOption[];
  onClose: () => void;
}> = ({ plan, modules, onClose }) => {
  if (!plan) return null;
  return (
    <>
      <div className={styles.drawerBackdrop} onClick={onClose} />
      <aside className={styles.drawer} aria-label="Plan details">
        <div className={styles.drawerHeader}>
          <div>
            <h3 className={styles.drawerName}>{plan.name} Plan</h3>
            <span className={styles.drawerId}>{plan.id}</span>
          </div>
          <button className={styles.drawerClose} onClick={onClose} aria-label="Close">
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>

        <div className={styles.drawerBody}>
          <div className={styles.drawerStats}>
            <div className={styles.drawerStatItem}>
              <span className={styles.drawerStatLabel}>Monthly</span>
              <span className={styles.drawerStatValue}>${plan.monthlyPrice}</span>
            </div>
            <div className={styles.drawerStatItem}>
              <span className={styles.drawerStatLabel}>Yearly</span>
              <span className={styles.drawerStatValue}>${plan.yearlyPrice}/mo</span>
            </div>
            <div className={styles.drawerStatItem}>
              <span className={styles.drawerStatLabel}>MRR</span>
              <span className={styles.drawerStatValue}>${plan.mrr.toLocaleString()}</span>
            </div>
          </div>

          <section className={styles.drawerSection}>
            <h4 className={styles.drawerSectionTitle}>Plan Limits</h4>
            {[
              { label: 'Max APIs', value: String(plan.maxApis) },
              { label: 'Max Agents', value: String(plan.maxAgents) },
              { label: 'Support Level', value: plan.supportLevel },
              { label: 'Active Subscribers', value: String(plan.subscribers) },
              { label: 'Status', value: plan.status },
              { label: 'Created', value: plan.created },
            ].map(row => (
              <div key={row.label} className={styles.drawerRow}>
                <span className={styles.drawerRowLabel}>{row.label}</span>
                <span className={styles.drawerRowValue}>{row.value}</span>
              </div>
            ))}
          </section>

          <section className={styles.drawerSection}>
            <h4 className={styles.drawerSectionTitle}>
              Enabled Modules ({plan.modules.length})
            </h4>
            <div className={styles.drawerModules}>
              {modules.map(m => {
                const included = plan.modules.includes(m.name);
                return (
                  <div
                    key={m.id}
                    className={`${styles.drawerModuleRow} ${
                      included
                        ? styles.drawerModuleIncluded
                        : styles.drawerModuleExcluded
                    }`}
                  >
                    {included ? <Check size={12} /> : <Minus size={12} />}
                    <span>{m.name}</span>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </aside>
    </>
  );
};

const DeactivateModal: React.FC<{
  plan: Plan | null;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ plan, onClose, onConfirm }) => (
  <BaseModal
    isOpen={!!plan}
    onClose={onClose}
    title="Delete Plan"
    subtitle={plan?.name}
    size="sm"
    footer={
      <>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={onConfirm}>
          Delete
        </Button>
      </>
    }
  >
    {plan && (
      <div className={styles.confirmBody}>
        <div className={`${styles.confirmIcon} ${styles.confirmIconDanger}`}>
          <XCircle size={24} />
        </div>
        <p className={styles.confirmText}>
          Are you sure you want to delete the <strong>{plan.name}</strong> plan? This action
          cannot be undone.
        </p>
      </div>
    )}
  </BaseModal>
);

interface PlanFormData {
  name: string;
  slug: string;
  monthlyPrice: string;
  yearlyPrice: string;
  maxHotel: string;
  maxFlights: string;
  mrr: string;
  subscribers: string;
  supportLevel: string;
  status: string;
  modules: string[];
}

const emptyForm: PlanFormData = {
  name: '',
  slug: '',
  monthlyPrice: '',
  yearlyPrice: '',
  maxHotel: '',
  maxFlights: '',
  mrr: '',
  subscribers: '',
  supportLevel: 'Email',
  status: 'active',
  modules: [],
};

function planToForm(p: Plan): PlanFormData {
  return {
    name: p.name,
    slug: String(p.slug),
    monthlyPrice: String(p.monthlyPrice),
    yearlyPrice: String(p.yearlyPrice),
    maxHotel: String(p.maxApis),
    maxFlights: String(p.maxAgents),
    mrr: String(p.mrr),
    subscribers: String(p.subscribers),
    supportLevel: p.supportLevel,
    status: p.status === 'Active' ? 'active' : 'inactive',
    modules: [...p.modules],
  };
}

const PlanFormModal: React.FC<{
  isOpen: boolean;
  editingPlan: Plan | null;
  onClose: () => void;
  onSubmit: (data: PlanFormData) => void;
  allModules: ModuleOption[];
}> = ({ isOpen, editingPlan, onClose, onSubmit, allModules }) => {
  const [form, setForm] = useState<PlanFormData>(
    editingPlan ? planToForm(editingPlan) : emptyForm,
  );

  useEffect(() => {
    setForm(editingPlan ? planToForm(editingPlan) : emptyForm);
  }, [editingPlan, isOpen]);

  const set =
    (k: keyof PlanFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }));

  const toggleModule = (m: string) => {
    setForm(f => ({
      ...f,
      modules: f.modules.includes(m)
        ? f.modules.filter(x => x !== m)
        : [...f.modules, m],
    }));
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={editingPlan ? 'Edit Plan' : 'Create New Plan'}
      subtitle={
        editingPlan ? `Editing ${editingPlan.name} plan` : 'Define a new subscription plan'
      }
      size="lg"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            icon={editingPlan ? <Edit3 size={14} /> : <Plus size={14} />}
            onClick={() => onSubmit(form)}
          >
            {editingPlan ? 'Save Changes' : 'Create Plan'}
          </Button>
        </>
      }
    >
      <div className={styles.formGrid3}>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Plan Name <span className={styles.req}>*</span>
          </label>
          <input
            className={styles.input}
            placeholder="e.g. Business"
            value={form.name}
            onChange={set('name')}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Slug <span className={styles.req}>*</span>
          </label>
          <input
            className={styles.input}
            placeholder="e.g. starter"
            value={form.slug}
            onChange={set('slug')}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Monthly Price ($) <span className={styles.req}>*</span>
          </label>
          <input
            className={styles.input}
            type="number"
            placeholder="99"
            value={form.monthlyPrice}
            onChange={set('monthlyPrice')}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Yearly Price ($/mo) <span className={styles.req}>*</span>
          </label>
          <input
            className={styles.input}
            type="number"
            placeholder="79"
            value={form.yearlyPrice}
            onChange={set('yearlyPrice')}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Max hotels <span className={styles.req}>*</span>
          </label>
          <input
            className={styles.input}
            type="number"
            placeholder="3"
            value={form.maxHotel}
            onChange={set('maxHotel')}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Max flights <span className={styles.req}>*</span>
          </label>
          <input
            className={styles.input}
            type="number"
            placeholder="15"
            value={form.maxFlights}
            onChange={set('maxFlights')}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Subscribers <span className={styles.req}>*</span>
          </label>
          <input
            className={styles.input}
            type="number"
            placeholder="0"
            value={form.subscribers}
            onChange={set('subscribers')}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            MRR <span className={styles.req}>*</span>
          </label>
          <input
            className={styles.input}
            type="number"
            placeholder="100"
            value={form.mrr}
            onChange={set('mrr')}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Support Level <span className={styles.req}>*</span>
          </label>
          <select
            className={styles.select}
            value={form.supportLevel}
            onChange={set('supportLevel')}
          >
            <option value="Email">Email</option>
            <option value="Priority Email">Priority Email</option>
            <option value="Dedicated Account Manager">
              Dedicated Account Manager
            </option>
          </select>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Status <span className={styles.req}>*</span>
          </label>
          <select
            className={styles.select}
            value={form.status}
            onChange={set('status')}
          >
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
        </div>
      </div>

      <div className={styles.modulesSection}>
        <label className={styles.label}>Enabled Modules</label>
        <div className={styles.modulesGrid}>
          {allModules.map(m => {
            const active = form.modules.includes(m.name);
            return (
              <button
                key={m.id}
                type="button"
                className={`${styles.moduleToggle} ${
                  active ? styles.moduleToggleActive : ''
                }`}
                onClick={() => toggleModule(m.name)}
              >
                {active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                {m.name}
              </button>
            );
          })}
        </div>
      </div>
    </BaseModal>
  );
};

const Subscriptions: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>(INITIAL_PLANS);
  const [allModules, setAllModules] = useState<ModuleOption[]>([]);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [detailPlan, setDetailPlan] = useState<Plan | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Plan | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const PER_PAGE = 8;

  // GET plans + modules
  useEffect(() => {
    const fetchAll = async () => {
      try {
        // plans
        const BASE_URL = process.env.REACT_APP_API_BASE_URL;
        const resPlans = await fetch(`${BASE_URL}/fly/get`);
        const jsonPlans = await resPlans.json();

        if (resPlans.ok) {
          const rows = Array.isArray(jsonPlans.data) ? jsonPlans.data : [];
          const mappedPlans: Plan[] = rows.map((row: any) => ({
            id: String(row.id),
            name: row.name,
            slug: row.slug,
            monthlyPrice: Number(row.monthlyprice ?? row.monthlyPrice ?? 0),
            yearlyPrice: Number(row.yearlyprice ?? row.yearlyPrice ?? 0),
            maxApis: Number(row.maxhotel ?? row.maxHotel ?? 0),
            maxAgents: Number(row.maxflights ?? row.maxFlights ?? 0),
            modules: Array.isArray(row.modules) ? row.modules : [],
            supportLevel: row.supportlevel ?? row.supportLevel ?? 'Email',
            status:
              (row.status ?? 'active').toLowerCase() === 'active'
                ? 'Active'
                : 'Inactive',
            subscribers: Number(row.subscribers ?? 0),
            mrr: Number(row.mrr ?? 0),
            created: row.created_at
              ? new Date(row.created_at).toLocaleDateString()
              : '',
          }));
          setPlans(mappedPlans);
        } else {
          console.error('get plans error', jsonPlans);
        }

        // modules
        //  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
        // const resPlans = await fetch(`${BASE_URL}/fly/get`);
        const resModules = await fetch(`${BASE_URL}/module/get`);
        const jsonModules = await resModules.json();

        if (resModules.ok) {
          const rowsM = Array.isArray(jsonModules.data) ? jsonModules.data : [];
          const mappedModules: ModuleOption[] = rowsM
            .filter(
              (m: any) =>
                (m.status ?? 'active').toLowerCase() === 'active',
            )
            .map((m: any) => ({
              id: String(m.id),
              name: m.name,
              status: (m.status ?? 'active') as string,
            }));
          setAllModules(mappedModules);
        } else {
          console.error('get modules error', jsonModules);
        }
      } catch (e) {
        console.error('fetch plans/modules error', e);
      }
    };

    fetchAll();
  }, []);

  const refreshPlans = async () => {
    try {
      
      const res = await fetch(`${BASE_URL}/fly/get`);
      const json = await res.json();
      if (!res.ok) return;
      const rows = Array.isArray(json.data) ? json.data : [];
      const mapped: Plan[] = rows.map((row: any) => ({
        id: String(row.id),
        name: row.name,
        slug: row.slug,
        monthlyPrice: Number(row.monthlyprice ?? row.monthlyPrice ?? 0),
        yearlyPrice: Number(row.yearlyprice ?? row.yearlyPrice ?? 0),
        maxApis: Number(row.maxhotel ?? row.maxHotel ?? 0),
        maxAgents: Number(row.maxflights ?? row.maxFlights ?? 0),
        modules: Array.isArray(row.modules) ? row.modules : [],
        supportLevel: row.supportlevel ?? row.supportLevel ?? 'Email',
        status:
          (row.status ?? 'active').toLowerCase() === 'active'
            ? 'Active'
            : 'Inactive',
        subscribers: Number(row.subscribers ?? 0),
        mrr: Number(row.mrr ?? 0),
        created: row.created_at
          ? new Date(row.created_at).toLocaleDateString()
          : '',
      }));
      setPlans(mapped);
    } catch (e) {
      console.error(e);
    }
  };

  const totalMrr = plans.reduce((s, p) => s + p.mrr, 0);
  const totalSubs = plans.reduce((s, p) => s + p.subscribers, 0);
  const activeSubs = SUBSCRIBERS.filter(s => s.status === 'Active').length;
  const trialSubs = SUBSCRIBERS.filter(s => s.status === 'Trial').length;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return SUBSCRIBERS.filter(
      s =>
        (!q ||
          s.tenantName.toLowerCase().includes(q) ||
          s.email.toLowerCase().includes(q) ||
          s.id.toLowerCase().includes(q)) &&
        (planFilter === 'All' || s.plan === planFilter) &&
        (statusFilter === 'All' || s.status === statusFilter),
    );
  }, [search, planFilter, statusFilter]);

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  const clearFilters = () => {
    setSearch('');
    setPlanFilter('All');
    setStatusFilter('All');
    setPage(1);
  };
  const hasFilters =
    search || planFilter !== 'All' || statusFilter !== 'All';

  const openCreate = () => {
    setEditingPlan(null);
    setFormOpen(true);
  };
  const openEdit = (p: Plan) => {
    setEditingPlan(p);
    setFormOpen(true);
  };

  // CREATE / UPDATE
  const handleSubmitPlan = async (data: PlanFormData) => {
    try {
      const body = {
        name: data.name,
        slug: data.slug,
        monthlyPrice: Number(data.monthlyPrice),
        yearlyPrice: Number(data.yearlyPrice),
        maxHotel: Number(data.maxHotel),
        maxFlights: Number(data.maxFlights),
        supportLevel: data.supportLevel,
        status: data.status,
        subscribers: Number(data.subscribers),
        mrr: Number(data.mrr),
         modules: Array.isArray(data.modules) ? data.modules : [],
      };

      const isEdit = !!editingPlan;
     const url = isEdit
      ? `${BASE_URL}/fly/update/${editingPlan?.id}`
      : `${BASE_URL}/fly/add`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const json = await res.json();
      console.log(
        isEdit ? 'update plan response' : 'create plan response',
        json,
      );

      if (!res.ok) {
        toast.error(
          json.message ||
            (isEdit
              ? 'Failed to update plan'
              : 'Failed to create plan'),
        );
        return;
      }

      toast.success(
        isEdit
          ? 'Plan updated successfully'
          : 'Plan created successfully',
      );
      await refreshPlans();
      setFormOpen(false);
      setEditingPlan(null);
    } catch (err) {
      console.error('create/update plan error', err);
    }
  };

  // DELETE
  const handleDeletePlan = async () => {
    if (!deactivateTarget) return;
    try {
      const res = await fetch(
        `${BASE_URL}/fly/delete/${deactivateTarget.id}`,
        {
          method: 'DELETE',
        },
      );
      const json = await res.json();
      console.log('delete plan response', json);
      if (!res.ok) {
        toast.error(json.message || 'Failed to delete plan');
        return;
      }
      toast.success('Plan deleted successfully');
      await refreshPlans();
      setDeactivateTarget(null);
    } catch (e) {
      console.error('delete plan error', e);
    }
  };

  const columns: Column<Subscriber>[] = [
    {
      key: 'subscriber',
      header: 'SUBSCRIBER',
      render: s => (
        <div className={styles.subCell}>
          <div className={styles.subAvatar}>
            {s.tenantName.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className={styles.subName}>{s.tenantName}</div>
            <div className={styles.subEmail}>{s.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'plan',
      header: 'PLAN',
      render: s => {
        const variant = s.plan.toLowerCase() as any;
        return (
          <StatusBadge
            variant={
              variant === 'standard'
                ? 'pro'
                : variant === 'advanced'
                ? 'enterprise'
                : 'starter'
            }
            size="sm"
            dot={false}
          />
        );
      },
    },
    {
      key: 'status',
      header: 'STATUS',
      render: s => (
        <StatusBadge variant={subStatusVariant(s.status)} size="sm" />
      ),
    },
    {
      key: 'billing',
      header: 'BILLING',
      render: s => (
        <span className={styles.billingChip}>{s.billingCycle}</span>
      ),
    },
    {
      key: 'amount',
      header: 'AMOUNT',
      render: s => (
        <span
          className={`${styles.numericCell} ${
            s.amount === 0 ? styles.numericMuted : ''
          }`}
        >
          {s.amount > 0 ? `$${s.amount}/mo` : '—'}
        </span>
      ),
    },
    {
      key: 'nextBilling',
      header: 'NEXT BILLING',
      render: s => (
        <span className={styles.dateText}>{s.nextBilling}</span>
      ),
    },
    {
      key: 'started',
      header: 'STARTED',
      render: s => (
        <span className={styles.dateText}>{s.started}</span>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Subscriptions / Plans</h1>
          <p className={styles.pageSubtitle}>
            Manage subscription plans and monitor subscriber activity
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="secondary"
            size="md"
            icon={<Download size={14} />}
          >
            Export
          </Button>
          <Button
            variant="primary"
            size="md"
            icon={<Plus size={14} />}
            onClick={openCreate}
          >
            Create Plan
          </Button>
        </div>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Total MRR</span>
            <span
              className={`${styles.statIcon} ${styles.statIconAccent}`}
            >
              <TrendingUp size={15} />
            </span>
          </div>
          <div
            className={`${styles.statValue} ${styles.statValueAccent}`}
          >
            ${totalMrr.toLocaleString()}
          </div>
          <div className={styles.statSub}>Across all plans</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Total Subscribers</span>
            <span className={styles.statIcon}>
              <Users size={15} />
            </span>
          </div>
          <div className={styles.statValue}>{totalSubs}</div>
          <div className={styles.statSub}>
            {activeSubs} active, {trialSubs} on trial
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Active Plans</span>
            <span className={styles.statIcon}>
              <CreditCard size={15} />
            </span>
          </div>
          <div className={styles.statValue}>
            {plans.filter(p => p.status === 'Active').length}
          </div>
          <div className={styles.statSub}>{plans.length} total configured</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Avg Revenue / Tenant</span>
            <span className={styles.statIcon}>
              <Zap size={15} />
            </span>
          </div>
          <div className={styles.statValue}>
            ${totalSubs > 0 ? Math.round(totalMrr / totalSubs) : 0}
          </div>
          <div className={styles.statSub}>Per active subscriber</div>
        </div>
      </div>

      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Plan Configuration</h2>
        <p className={styles.sectionSub}>{plans.length} plans configured</p>
      </div>

      <div className={styles.plansGrid}>
        {plans.map(p => (
          <PlanCard
            key={p.id}
            plan={p}
            onEdit={openEdit}
            onDeactivate={setDeactivateTarget}
            onView={setDetailPlan}
          />
        ))}
      </div>

      <div className={styles.sectionHeader} style={{ marginTop: 28 }}>
        <h2 className={styles.sectionTitle}>All Subscribers</h2>
        <p className={styles.sectionSub}>
          {SUBSCRIBERS.length} total subscriptions
        </p>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <Search size={14} className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search subscribers…"
            />
            {search && (
              <button
                className={styles.searchClear}
                onClick={() => setSearch('')}
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div className={styles.filterPills}>
            {(
              ['All', 'Active', 'Trial', 'Expired', 'Cancelled'] as const
            ).map(s => (
              <button
                key={s}
                className={`${styles.pill} ${
                  statusFilter === s ? styles.pillActive : ''
                }`}
                onClick={() => {
                  setStatusFilter(s);
                  setPage(1);
                }}
              >
                {s}
              </button>
            ))}
          </div>

          <div className={styles.selects}>
            <select
              className={styles.select}
              value={planFilter}
              onChange={e => {
                setPlanFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="All">All Plans</option>
              <option value="Starter">Starter</option>
              <option value="Standard">Standard</option>
              <option value="Advanced">Advanced</option>
            </select>
          </div>
        </div>

        <div className={styles.resultsBar}>
          <span className={styles.resultsText}>
            Showing <strong>{paged.length}</strong> of{' '}
            <strong>{filtered.length}</strong> subscribers
          </span>
          {hasFilters && (
            <button
              className={styles.clearFilters}
              onClick={clearFilters}
            >
              <X size={10} /> Clear filters
            </button>
          )}
        </div>

        <DataTable
          columns={columns}
          data={paged}
          keyExtractor={s => s.id}
          emptyState={
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>No subscribers found</p>
              <p className={styles.emptySub}>
                Try adjusting your search or filters
              </p>
              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                >
                  Clear all filters
                </Button>
              )}
            </div>
          }
        />

        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={filtered.length}
          perPage={PER_PAGE}
          onPageChange={p => setPage(p)}
        />
      </div>

      <PlanDetailDrawer
        plan={detailPlan}
        modules={allModules}
        onClose={() => setDetailPlan(null)}
      />
      <DeactivateModal
        plan={deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeletePlan}
      />
      <PlanFormModal
        isOpen={formOpen}
        editingPlan={editingPlan}
        onClose={() => {     
          setFormOpen(false);
          setEditingPlan(null);
        }}
        onSubmit={handleSubmitPlan}
        allModules={allModules}
      />
    </div>
  );
};

export default Subscriptions;
